import React, { useEffect, useState } from 'react';
import CustomContainer from '../CustomElements/CustomContainer';
import CustomTitle from '../CustomElements/CustomTitle';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
import FormContainer from '../CustomElements/FormContainer';
import Input from '../CustomElements/Input';
import CustomButton from '../CustomElements/CustomButton';
import { handleUniversalChange } from '../../utils/formUtils';
import { useDispatch, useSelector } from 'react-redux';
import { load, remove } from '../../redux/actions/loader.action';
import { useButtonValidation } from '../../customHook/useButtonValidation';
import { newFolderFromServer } from '../../redux/actions/files.action';

/**
 * Компонент модального окна для создания новой папки.
 *
 * @component
 * @example
 * return (
 *   <AddFolderModal onClose={handleClose} currentFolder={{ id: "123", name: "Документы" }} />
 * )
 *
 * @param {Object} props - Свойства компонента.
 * @param {Function} props.onClose - Функция закрытия модального окна.
 * @param {Object} props.currentFolder - Объект текущей папки, в которой создается новая папка.
 * @param {string} props.currentFolder.id - ID текущей папки.
 * @param {string} props.currentFolder.name - Название текущей папки.
 * @returns {JSX.Element} Модальное окно для создания папки.
 */
const AddFolderModal = ({ onClose, currentFolder, t }) => {
  // Инициализация Redux dispatch
  const dispatch = useDispatch();

  // Получение данных о состоянии загрузки из Redux store
  const loader = useSelector((state) => state.loader);

  // Состояние для отображения страницы подтверждения после успешного создания папки
  const [addSuccess, setAddSuccess] = useState(false);

  // Локальное состояние формы (содержит имя папки и ID родительской папки)
  const [formData, setFormData] = useState({ name: '', parentId: currentFolder?.id || null });

  // Проверка, можно ли активировать кнопку "Создать"
  const isButtonDisabled = useButtonValidation(formData, ['parentId']);

  /**
   * Обрабатывает создание папки.
   * - Переводит компонент в состояние загрузки.
   * - Отправляет запрос на создание новой папки.
   *
   * @param {Event} event - Событие клика.
   */
  const handleCreate = (event) => {
    event.preventDefault();
    // Переводим компонент в состояние загрузки
    dispatch(load());
    // Отправка запроса на сервер
    dispatch(newFolderFromServer(formData));
  };

  /**
   * Эффект отслеживает изменения состояния загрузки.
   * - Если загрузка завершилась успешно, показываем страницу подтверждения.
   * - Очищаем состояние загрузки при размонтировании компонента.
   */
  useEffect(() => {
    if (!loader?.isLoading && loader?.isSuccess) {
      setAddSuccess(true);
    }

    return () => {
      dispatch(remove()); // Очистка состояния загрузки при закрытии компонента
    };
  }, [loader?.isLoading, loader?.isSuccess, dispatch]);

  return (
    <CustomContainer>
      {/* Заголовок окна */}
      <CustomTitle titleText={t?.createFolderTitle || 'СОЗДАНИЕ НОВОЙ ПАПКИ'} />

      {/* Если папка успешно создана, показываем подтверждающий экран */}
      {addSuccess ? (
        <ConfirmationPage
          confirmText={t?.createFolderSuccess || 'Новая папка добавлена!'}
          onConfirm={onClose}
          addNew={'К списку'}
        />
      ) : (
        <FormContainer columns={1}>
          {/* Поле ввода для имени папки */}
          <Input
            column="center"
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            name="name"
            label={t?.createFolderLabel || 'Папка *'}
            placeholder={t?.createFolderPlaceholder || 'Введите имя папки'}
            width="100%"
            height="var(--sadr-font-size-60)"
          />

          {/* Кнопка создания папки */}
          <CustomButton
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
            margin={'0 0 var(--sadr-font-size-20) 0'}
            text={t?.createFolderButton || 'Создать'}
            onClick={handleCreate}
            disabled={isButtonDisabled}
            isButton
          />

          {/* Кнопка отмены */}
          <CustomButton
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
            margin={'0 0 var(--sadr-font-size-20) 0'}
            text={t?.cancelButton || 'Отмена'}
            onClick={onClose}
            disabled={false}
            isButton
            colorText="#1C221D"
            color="var(--sadr-background-secondary)"
            border
          />
        </FormContainer>
      )}
    </CustomContainer>
  );
};

export default AddFolderModal;
